/* Studioflow landing background — a 10,000-pixel storm that swarms the cursor.
 *
 * Each point drifts with turbulence; near the pointer they're pulled in (with a
 * swirl) and brighten with speed, so a dense pixel-storm gathers around the
 * cursor and disperses when it stops. Sits behind the rotating blade.
 *
 * Perf: at 10k points the cost is setting fillStyle, so points are bucketed
 * into 8 brightness levels and drawn with one fillStyle per level (~8 style
 * changes/frame instead of 10k). Plain canvas 2D. Landing pages only
 * (data-storm). Pauses when hidden; respects reduced-motion.
 */
(function () {
  "use strict";

  var canvas = document.getElementById("bg-storm");
  if (!canvas) return;
  if (document.body.dataset.storm !== "true") return;

  var reduce =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reduce) return;

  var ctx = canvas.getContext("2d");
  var dpr = Math.min(window.devicePixelRatio || 1, 2);
  var W = 0, H = 0;
  var COUNT = 10000;
  var R = 430;                 // cursor influence radius
  var LEVELS = 8;
  var TWO_PI = 6.283185307179586;
  var mouse = { x: -1e9, y: -1e9, active: false };

  // Pre-built colours per brightness level. Dark theme: faint blue-grey ->
  // bright white. Light theme (day): light slate -> near-black so the storm
  // stays visible on a white background. Rebuilt when the theme toggles.
  var COLORS = [];
  function buildColors() {
    COLORS.length = 0;
    var light =
      document.documentElement.getAttribute("data-theme") === "light";
    for (var i = 0; i < LEVELS; i++) {
      var sp = i / (LEVELS - 1);
      var r, g, b;
      // Fully opaque in both themes — colour (not alpha) carries the speed cue.
      var a = "1";
      if (light) {
        // Pitch black — near-black at rest, pure #000 at speed (around the cursor).
        r = (34 - sp * 34) | 0;
        g = (34 - sp * 34) | 0;
        b = (38 - sp * 38) | 0;
      } else {
        // Light blue-grey -> pure white, fully opaque so every particle reads on black.
        r = (170 + sp * 85) | 0;
        g = (180 + sp * 75) | 0;
        b = (210 + sp * 45) | 0;
      }
      COLORS.push("rgba(" + r + "," + g + "," + b + "," + a + ")");
    }
  }
  buildColors();

  // Let the theme toggle (base.html) recolour the storm without a reload.
  window.sfRebuildStorm = buildColors;

  // Flat typed arrays keep 10k points cheap and allocation-free per frame.
  var px, py, vx, vy, ps, pl;
  function seed() {
    px = new Float32Array(COUNT);
    py = new Float32Array(COUNT);
    vx = new Float32Array(COUNT);
    vy = new Float32Array(COUNT);
    ps = new Uint8Array(COUNT);
    pl = new Uint8Array(COUNT);
    for (var i = 0; i < COUNT; i++) {
      px[i] = Math.random() * W;
      py[i] = Math.random() * H;
      // Round dots — fine 1–2px grain (matches the detailed night-mode look).
      ps[i] = Math.random() < 0.3 ? 2 : 1;
    }
  }

  function size() {
    W = window.innerWidth;
    H = window.innerHeight;
    // Zero-sized viewport (offscreen/background tab at load) — keep polling
    // until the window is real, then size and seed for real.
    if (!W || !H) {
      if (!px) seed();
      setTimeout(size, 200);
      return;
    }
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    seed();
  }

  function step() {
    ctx.clearRect(0, 0, W, H);
    var i, dx, dy, d, f, sp, lvl;
    var ma = mouse.active, mx = mouse.x, my = mouse.y;

    for (i = 0; i < COUNT; i++) {
      // turbulence
      vx[i] += (Math.random() - 0.5) * 0.5;
      vy[i] += (Math.random() - 0.5) * 0.5;

      // cursor swarm (pull + swirl)
      if (ma) {
        dx = mx - px[i];
        dy = my - py[i];
        d = Math.sqrt(dx * dx + dy * dy) + 0.01;
        if (d < R) {
          f = 1 - d / R;
          vx[i] += (dx / d) * f * 1.2 + (-dy / d) * f * 0.55;
          vy[i] += (dy / d) * f * 1.2 + (dx / d) * f * 0.55;
        }
      }

      vx[i] *= 0.86;
      vy[i] *= 0.86;
      px[i] += vx[i];
      py[i] += vy[i];

      if (px[i] < 0) px[i] += W; else if (px[i] > W) px[i] -= W;
      if (py[i] < 0) py[i] += H; else if (py[i] > H) py[i] -= H;

      sp = (Math.abs(vx[i]) + Math.abs(vy[i])) / 6;
      lvl = (sp * (LEVELS - 1)) | 0;
      if (lvl > LEVELS - 1) lvl = LEVELS - 1;
      pl[i] = lvl;
    }

    // One fillStyle per brightness bucket; round dots batched into a single path.
    for (lvl = 0; lvl < LEVELS; lvl++) {
      ctx.fillStyle = COLORS[lvl];
      ctx.beginPath();
      for (i = 0; i < COUNT; i++) {
        if (pl[i] === lvl) {
          var rr = ps[i] * 0.5;
          ctx.moveTo(px[i] + rr, py[i]);
          ctx.arc(px[i], py[i], rr, 0, TWO_PI);
        }
      }
      ctx.fill();
    }
    raf = requestAnimationFrame(step);
  }

  var raf = null;
  function start() { if (raf == null) raf = requestAnimationFrame(step); }
  function stop() { if (raf != null) { cancelAnimationFrame(raf); raf = null; } }

  window.addEventListener("pointermove", function (e) {
    mouse.x = e.clientX;
    mouse.y = e.clientY;
    mouse.active = true;
  }, { passive: true });
  window.addEventListener("pointerout", function () { mouse.active = false; });

  document.addEventListener("visibilitychange", function () {
    if (document.hidden) stop(); else start();
  });
  var rt = null;
  window.addEventListener("resize", function () {
    clearTimeout(rt); rt = setTimeout(size, 150);
  });

  // Public toggle (used by the nav FX button); preference persists per visitor.
  window.sfStormToggle = function (on) {
    canvas.style.display = on ? "" : "none";
    if (on) start(); else stop();
    try { localStorage.setItem("tf-storm", on ? "on" : "off"); } catch (e) {}
  };

  size();
  var savedOff = false;
  try { savedOff = localStorage.getItem("tf-storm") === "off"; } catch (e) {}
  if (savedOff) { canvas.style.display = "none"; } else { start(); }
})();
